/**
 * Cloud live player for the shared Alert Hexagon page.
 *
 * HexStreamProvider is a thin adapter over the app-level NodeStreamProvider (mounted in
 * Layout) — it does not own a NodeConnectionManager. The single shared connection is kept warm
 * for the whole session and reused across pages, so opening the Hexagon page (or clicking a
 * hexagon) only adds a track over the existing WS+PC. HexLivePlayer is the injected
 * PlayerComponent: it ref-counts a camera subscription (shared ref-count + linger live in the
 * app provider) and renders LiveStreamPlayer for its stream.
 *
 * WHICH SITE a camera streams from comes from the camera id itself. The per-site board passes
 * plain camera ids, which mean "the selected site"; the all-sites board passes site-qualified
 * `nodeId::cameraId` keys, because its hexagons come from every node at once and a preview has
 * to open on the camera's OWN node — not on whichever site happens to be selected (on that
 * board, none is). Both shapes resolve here, so one player serves both boards.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useNode } from '../../contexts/NodeContext';
import { type NodeConnectionManager } from '../../services/node-connection-manager';
import { useNodeStream } from '../../contexts/NodeStreamContext';
import { splitSiteCameraKey } from '../../api/multiSiteHexAlerts.adapter';
import LiveStreamPlayer from '../LiveStreamPlayer';

/** A board camera id resolved to the node that can actually stream it. */
interface Target {
  nodeId: string;
  nodeName: string;
  cameraId: string;
}

interface Controls {
  /** null when the key names no reachable site — the caller renders nothing. */
  resolve: (cameraKey: string) => Target | null;
  add: (cameraKey: string, label?: string) => void;
  remove: (cameraKey: string) => void;
  manager: () => NodeConnectionManager | null;
}

const ControlsCtx = createContext<Controls | null>(null);

export function HexStreamProvider({ children }: { children: ReactNode }) {
  const { selectedNode, nodes } = useNode();
  const { subscribe, unsubscribe, manager } = useNodeStream();

  // Node names are only labels for the connection manager, and NodeContext re-polls the
  // roster every 10s with a fresh array — so they are held in a ref rather than in the
  // memo's dependencies. Were `controls` to change on every poll, each player's effect would
  // re-run and every live tile on the board would unsubscribe and re-subscribe, twice a
  // minute, for a name it already had.
  const nameByIdRef = useRef<Record<string, string>>({});
  nameByIdRef.current = useMemo(() => {
    const m: Record<string, string> = {};
    nodes.forEach((n) => (m[n.nodeId] = n.name));
    return m;
  }, [nodes]);

  const controls = useMemo<Controls>(() => {
    const resolve = (cameraKey: string): Target | null => {
      const { nodeId, cameraId } = splitSiteCameraKey(cameraKey);
      // No site in the key = the per-site board, whose cameras are the selected node's.
      if (!nodeId) {
        return selectedNode
          ? { nodeId: selectedNode.nodeId, nodeName: selectedNode.name, cameraId }
          : null;
      }
      return { nodeId, nodeName: nameByIdRef.current[nodeId] || nodeId, cameraId };
    };

    return {
      resolve,
      add: (cameraKey, label) => {
        const t = resolve(cameraKey);
        if (t) subscribe(t.nodeId, t.nodeName, t.cameraId, label);
      },
      remove: (cameraKey) => {
        const t = resolve(cameraKey);
        if (t) unsubscribe(t.nodeId, t.cameraId);
      },
      manager,
    };
  }, [selectedNode, subscribe, unsubscribe, manager]);

  return <ControlsCtx.Provider value={controls}>{children}</ControlsCtx.Provider>;
}

interface HexLivePlayerProps {
  /** Plain camera id (per-site board) or `nodeId::cameraId` (all-sites board). */
  cameraId: string;
  label?: string;
  className?: string;
  showOverlay?: boolean;
  /** Fired when the live video actually starts playing (see HexPlayerComponent). */
  onStreamStart?: () => void;
}

export default function HexLivePlayer({ cameraId, label, className, onStreamStart }: HexLivePlayerProps) {
  const controls = useContext(ControlsCtx);
  const { streams, states } = useNodeStream();

  useEffect(() => {
    if (!controls) return;
    controls.add(cameraId, label);
    return () => controls.remove(cameraId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, controls]);

  const target = controls?.resolve(cameraId);
  if (!controls || !target) return <div className={className} />;

  const key = `${target.nodeId}:${target.cameraId}`;
  return (
    <LiveStreamPlayer
      stream={streams.get(key) || null}
      connectionState={states.get(key) || 'connecting'}
      label={label}
      className={className}
      onStalled={() => controls.manager()?.handleCameraStall(target.nodeId, target.cameraId)}
      onPlaying={() => {
        controls.manager()?.handleCameraPlaying(target.nodeId, target.cameraId);
        onStreamStart?.();
      }}
      onRetry={() => controls.manager()?.retryCamera(target.nodeId, target.cameraId)}
    />
  );
}
